'use client';

import React, { Suspense, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import Link from 'next/link';
import { signIn } from 'next-auth/react';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, Loader2 } from 'lucide-react';

const AppleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden>
    <path
      fill="currentColor"
      d="M16.365 1.43c0 1.14-.493 2.27-1.177 3.08-.744.9-1.99 1.57-2.987 1.57-.12 0-.23-.02-.3-.03-.01-.06-.04-.22-.04-.39 0-1.15.572-2.27 1.206-2.98.804-.94 2.142-1.64 3.248-1.68.03.13.05.28.05.43zm4.565 15.71c-.03.07-.463 1.58-1.518 3.12-.945 1.34-1.94 2.71-3.43 2.71-1.48 0-1.87-.8-3.64-.8-1.77 0-2 .74-3.63.74-1.55 0-2.37-.88-3.43-2.62-1.87-2.86-2.63-7.15-1.03-10.49 1.03-2.2 3.29-3.62 5.43-3.66 1.85-.08 3.39 1.08 3.86 1.08.46 0 2.22-1.35 4.28-1.35.91 0 3.73.35 5.52 3.28-.04.01-3.61 2.11-3.61 6.39z"
    />
  </svg>
);

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path
      fill="currentColor"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="currentColor"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="currentColor"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="currentColor"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

function SignInPageContent() {
  const t = useTranslations('auth');
  const locale = useLocale();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const oauthCallbackError = searchParams.get('error');
  const oauthBannerMessage =
    oauthCallbackError === 'OAuthAccountNotLinked'
      ? t('signIn.oauthAccountNotLinked')
      : oauthCallbackError && oauthCallbackError !== 'Configuration'
        ? t('signIn.oauthCallbackError', { code: oauthCallbackError })
        : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await signIn('credentials', {
        email: email.toLowerCase().trim(),
        password,
        redirect: false,
      });

      if (result?.error) {
        let errorMessage: string;
        if (result.error === 'CredentialsSignin') {
          if (result.code === 'email_not_verified') {
            errorMessage = t('signIn.emailNotVerified');
          } else if (result.code === 'account_suspended') {
            errorMessage = t('signIn.accountSuspended');
          } else {
            errorMessage = t('errors.invalidCredentials');
          }
        } else if (result.error === 'Configuration') {
          errorMessage = t('signIn.serverMisconfiguration');
        } else {
          errorMessage = result.error;
        }
        setError(errorMessage);
        setIsLoading(false);
      } else if (result?.ok) {
        // Laisser le serveur décider (requireAuthAndSubscription sur /dashboard) : évite la course
        // cookie / session sur /api/subscription/check juste après signIn, et évite un état client
        // désaligné avec la vérité DB.
        router.refresh();
        router.push(`/${locale}/dashboard`);
      } else {
        setError('Une erreur est survenue lors de la connexion');
        setIsLoading(false);
      }
    } catch (err: any) {
      console.error('Erreur de connexion:', err);
      setError(err?.message || 'Une erreur est survenue lors de la connexion');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-poker-darker via-poker-dark to-poker-darker flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Logo variant="light" />
          </div>
          <CardTitle className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('signIn.title')}
          </CardTitle>
          <CardDescription className="text-gray-600 dark:text-gray-400">
            {t('signIn.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {oauthBannerMessage && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-900 dark:text-amber-100 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{oauthBannerMessage}</span>
              </div>
            )}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('signIn.email')}
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value.toLowerCase())}
                required
                className="w-full"
                placeholder={t('signIn.emailPlaceholder')}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('signIn.password')}
              </label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full"
                placeholder={t('signIn.passwordPlaceholder')}
              />
              <div className="mt-2 text-right">
                <Link
                  href="/auth/forgot-password"
                  className="text-sm text-gold hover:text-gold-dark font-medium"
                >
                  {t('signIn.forgotPassword')}
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              variant="gold"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('signIn.loading')}
                </>
              ) : (
                t('signIn.submit')
              )}
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-gray-300 dark:border-gray-600" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white dark:bg-gray-800 px-2 text-gray-500 dark:text-gray-400">
                  {t('signIn.or')}
                </span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={async () => {
                await signIn('google', { callbackUrl: `/${locale}/dashboard` });
              }}
            >
              <GoogleIcon />
              <span className="ml-2">{t('signIn.continueWithGoogle')}</span>
            </Button>

            {process.env.NEXT_PUBLIC_APPLE_SIGNIN_ENABLED === '1' ? (
              <Button
                type="button"
                className="mt-2 w-full bg-black text-white hover:bg-black/90 border border-black dark:bg-white dark:text-black dark:border-white dark:hover:bg-white/90"
                onClick={async () => {
                  await signIn('apple', { callbackUrl: `/${locale}/dashboard` });
                }}
              >
                <AppleIcon />
                <span className="ml-2">{t('signIn.continueWithApple')}</span>
              </Button>
            ) : null}

            <div className="text-center text-sm text-gray-600 dark:text-gray-400">
              {t('signIn.noAccount')}{' '}
              <a
                href="/auth/signup"
                className="text-gold hover:text-gold-dark font-medium"
              >
                {t('signIn.signUp')}
              </a>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default function SignInPage() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-gradient-to-b from-poker-darker via-poker-dark to-poker-darker flex items-center justify-center p-4">
          <p className="text-gray-400 text-sm">…</p>
        </div>
      }
    >
      <SignInPageContent />
    </Suspense>
  );
}
